'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useProductContactActions } from '@/hooks/useProductContactActions';
import { PhotosViewModal } from '@/modals/PhotosViewModal';
import { useModals } from '@/contexts/ModalContext';
import { useUserData } from '@/contexts/LoginContext';
import { TelegramProductExtendedDto } from '@/generated';
import { fixed640Loader } from '@/utils/imageLoaders/imageLoader640';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';

const navButtonClass =
  'absolute top-1/2 z-10 size-8 -translate-y-1/2 rounded-full bg-white/90 text-text-primary shadow-[0_2px_12px_rgba(0,0,0,0.12)] backdrop-blur-sm transition-opacity hover:bg-white hover:text-text-primary disabled:opacity-0 md:size-9 lg:size-10';

type Props = {
  photos: string[];
  product: TelegramProductExtendedDto;
};

export function PhotosControl({ photos, product }: Props) {
  const t = useTranslations();
  const [activeIndex, setActiveIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const [loaded, setLoaded] = useState<Set<number>>(() => new Set());
  const { open, close } = useModals();

  const userData = useUserData();
  const { onClickWrite: writeContact, onClickCall: callContact } = useProductContactActions(product);
  const isOwnProduct = product.telegramUserId === userData?.id;
  const isArchived = product.status === 'Archived';

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setActiveIndex(api.selectedScrollSnap());
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (api && api.selectedScrollSnap() !== activeIndex) api.scrollTo(activeIndex);
  }, [api, activeIndex]);

  const markLoaded = useCallback((index: number) => {
    setLoaded((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  }, []);

  const onClickWrite = useCallback(() => {
    close('photos-modal');
    void writeContact();
  }, [close, writeContact]);

  const onClickCall = useCallback(() => {
    close('photos-modal');
    void callContact();
  }, [close, callContact]);

  const goToPhotos = useCallback(() => {
    open(
      'photos-modal',
      <PhotosViewModal
        onClickWrite={onClickWrite}
        onClickCall={onClickCall}
        showButtons={!isOwnProduct && !isArchived}
        photos={photos}
        activeIndexInit={activeIndex}
        onIndexChange={setActiveIndex}
        product={product}
        canFavorite={!isOwnProduct}
        onClose={() => {
          close('photos-modal');
        }}
      />,
      true,
      'md:h-unset h-unset absolute md:static my-0 top-0 bottom-0 md:top-unset md:m-0 md:max-h-full lg:max-h-[calc(100%-16px)] md:rounded-[1px] lg:rounded-[24px]',
    );
  }, [close, open, photos, isOwnProduct, isArchived, activeIndex, onClickCall, onClickWrite, product]);

  return (
    <div>
      <div className='group relative w-[95vw] lg:w-[599px] md:h-[406px] h-[343px] lg:h-[478px] overflow-hidden rounded-[8px] border border-solid border-border-light bg-surface-lighter'>
        <Carousel setApi={setApi} className='size-full [&_[data-slot=carousel-content]]:h-full'>
          <CarouselContent className='ms-0 h-full'>
            {photos.map((src, index) => {
              const isLoaded = loaded.has(index);
              return (
                <CarouselItem key={`${src}-${index}`} className='relative h-full ps-0'>
                  <Image
                    src={src}
                    alt=''
                    fill
                    priority={index === 0}
                    sizes='(min-width: 1024px) 599px, 95vw'
                    loader={fixed640Loader}
                    draggable={false}
                    onLoad={() => markLoaded(index)}
                    onClick={goToPhotos}
                    className={clsx('cursor-zoom-in object-contain select-none transition-opacity duration-300', {
                      'opacity-0': !isLoaded,
                      'opacity-100': isLoaded,
                      'blur-[2px] brightness-110': isArchived,
                    })}
                  />
                  {!isLoaded && (
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <Spinner className='size-[40px] text-main-green' />
                    </div>
                  )}
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>

        {photos.length > 1 && (
          <>
            <Button
              variant='ghost'
              size='icon'
              disabled={activeIndex === 0}
              onClick={() => api?.scrollPrev()}
              aria-label={t('photoViewer.prev')}
              className={clsx(navButtonClass, 'start-2 md:start-3')}
            >
              <IconChevronLeft className='size-5 rtl:rotate-180' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              disabled={activeIndex === photos.length - 1}
              onClick={() => api?.scrollNext()}
              aria-label={t('photoViewer.next')}
              className={clsx(navButtonClass, 'end-2 md:end-3')}
            >
              <IconChevronRight className='size-5 rtl:rotate-180' />
            </Button>
            <span
              aria-hidden
              className='pointer-events-none absolute bottom-3 end-3 z-10 rounded-full bg-black/55 px-2.5 py-1 text-xs font-medium text-white tabular-nums backdrop-blur-sm lg:hidden'
            >
              {activeIndex + 1} / {photos.length}
            </span>
          </>
        )}
      </div>
      <div className='md:mt-[38px] lg:flex hidden mt-[20px] max-w-[95vw] overflow-x-auto hide-scroll'>
        <div className='flex gap-2 min-w-fit flex-nowrap p-1'>
          {photos.map((src, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                type='button'
                key={`${src}-${index}`}
                onClick={() => setActiveIndex(index)}
                aria-current={isActive}
                className={clsx(
                  'relative block h-12 w-16 shrink-0 cursor-pointer overflow-hidden rounded-[4px] bg-surface-light transition outline-offset-2 focus-visible:outline-2 focus-visible:outline-main-green',
                  isActive ? 'opacity-100 outline-2 outline-main-green' : 'opacity-55 hover:opacity-100',
                )}
              >
                <Image
                  src={src}
                  alt=''
                  fill
                  sizes='64px'
                  draggable={false}
                  className={clsx('object-cover', { 'blur-[1px] brightness-110': isArchived })}
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
